'use client'

import { cn } from '@/lib/utils'

/**
 * Canonical invoice statuses per UI spec §3 (docs/specs/ui.md).
 *
 * The component accepts any string at runtime and falls back to a muted
 * "Unknown" chip for values outside this union.
 */
export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'created'
  | 'viewed'
  | 'overdue'
  | 'paid'
  | 'partially_paid'
  | 'expired'
  | 'email_failed'
  | 'payment_failed'
  | 'payment_pending'
  | 'voided'
  | 'refunded'
  | 'partially_refunded'
  | 'disputed'

interface StatusBadgeProps {
  status: InvoiceStatus | (string & {})
  className?: string
}

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; className: string }> = {
  draft: {
    label: 'Draft',
    className: 'bg-muted text-muted-foreground',
  },
  sent: {
    label: 'Sent',
    className: 'bg-info/10 text-info',
  },
  // Issued without emailing the customer
  // (create-invoice-without-sending-email, Q3): a teal tone distinct from
  // Sent's blue, Draft's grey, and the amber/green/red statuses. Uses the
  // semantic `--created` token like every sibling badge (not raw Tailwind).
  created: {
    label: 'Created',
    className: 'bg-created/10 text-created',
  },
  viewed: {
    label: 'Viewed',
    className: 'bg-info/10 text-info',
  },
  overdue: {
    label: 'Overdue',
    className: 'bg-warning/10 text-warning',
  },
  paid: {
    label: 'Paid',
    className: 'bg-success/10 text-success',
  },
  // Installment plans: first installment received, remaining schedule
  // still collecting — in-flight info palette, not the terminal paid
  // green, so the two stay distinguishable at a glance.
  partially_paid: {
    label: 'Partially Paid',
    className: 'bg-info/10 text-info',
  },
  expired: {
    label: 'Expired',
    className: 'bg-destructive/10 text-destructive/70',
  },
  email_failed: {
    label: 'Email Failed',
    className: 'bg-warning/10 text-warning',
  },
  payment_failed: {
    label: 'Payment Failed',
    className: 'bg-destructive/10 text-destructive',
  },
  // Async payment (bank debit/transfer/voucher) completed but not yet
  // confirmed by Stripe — money in motion, so the in-flight info palette
  // (same family as partially_paid), distinct from terminal paid green and
  // destructive payment_failed red.
  payment_pending: {
    label: 'Payment Pending',
    className: 'bg-info/10 text-info',
  },
  voided: {
    label: 'Voided',
    className: 'bg-destructive/10 text-destructive',
  },
  refunded: {
    label: 'Refunded',
    className: 'bg-destructive/10 text-destructive/70',
  },
  partially_refunded: {
    label: 'Partially Refunded',
    className: 'bg-destructive/10 text-destructive/70',
  },
  disputed: {
    label: 'Disputed',
    className: 'bg-destructive/10 text-destructive',
  },
}

const FALLBACK = {
  label: 'Unknown',
  className: 'bg-muted text-muted-foreground',
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config =
    (STATUS_CONFIG as Record<string, (typeof STATUS_CONFIG)[InvoiceStatus]>)[
      status
    ] ?? FALLBACK

  return (
    <span
      data-slot="status-badge"
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-caption font-medium',
        config.className,
        className,
      )}
    >
      {config.label}
    </span>
  )
}
